import React, { useState, useContext } from 'react';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import SearchIcon from '@mui/icons-material/Search';
import CartIcon from '@mui/icons-material/ShoppingCartOutlined';
import Box from '@mui/material/Box';
import NavigationDrawer from './NavigationDrawer';
import useScrollTrigger from '@mui/material/useScrollTrigger';
import Slide from '@mui/material/Slide';
import { useHistory } from 'react-router-dom';
import clxs from 'classnames';
import { uiContext } from 'contexts/UIContext';
import Logo from 'components/Helpers/Logo';
import QuickSearchResults from 'components/Search/QuickSearchResults';
import AltSearchInput from 'components/Search/AltSearchInput';
import useMediaQuery from '@mui/material/useMediaQuery';
import useTheme from '@mui/material/styles/useTheme';
import Dialog from '@mui/material/Dialog';

const useStyles: any = makeStyles(
  (theme: Theme) =>
    createStyles({
      root: {
        transition: 'transform 225ms cubic-bezier(0, 0, 0.2, 1) 0ms, background-color 0.5s, box-shadow 0.5s !important',
        backgroundColor: theme.palette.secondary.dark, // theme.palette.grey["A700"], // TODO - "#2E2E2E",
        backgroundImage: 'none',
        boxShadow: '0px 9px 40px rgba(0, 0, 0, 0.16)',
      },
      transparent: {
        backgroundColor: 'transparent',
        backgroundImage: 'none',
        boxShadow: 'none',
        // position: 'relative',
      },
      toolbar: {
        minHeight: 60,
        transition: 'min-height 0.5s !important',
      },
      phatToolbar: {
        [theme.breakpoints.up('md')]: {
          minHeight: 100,
        },
      },
      hideIcon: {
        [theme.breakpoints.up('lg')]: {
          display: 'none',
        },
      },
    }),
  { name: 'Menu' },
);

const HideOnScroll = (props: any) => {
  const { children } = props;
  const trigger = useScrollTrigger({
    threshold: 60,
  });
  return (
    <Slide key="slider" appear={false} direction="down" in={!trigger} exit={false}>
      {children}
    </Slide>
  );
};

export default function DenseAppBar() {
  const { stickyMenu, transparentMenu, showPhatHeader } = useContext(uiContext);
  const [showDrawer, setShowDrawer] = useState(false);
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [showSearchInput, setShowSearchInput] = useState(false);
  const classes = useStyles();
  const history = useHistory();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));
  const isLargeDesktop = useMediaQuery(theme.breakpoints.up('lg')); // Used to detect dedicated search
  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 60,
  });

  const useDedicated = isLargeDesktop && document.getElementById('dedicatedSearch');

  const toggleSearch = () => {
    if (useDedicated) {
      // On the super large desktop, we use the dedicated search...
      document.getElementById('dedicatedSearch')?.focus();
      document.getElementById('dedicatedSearch')?.click();
    } else {
      setShowSearchInput(!showSearchInput);
      setSearchQuery('');
    }
  };

  // the menu bar is transparent only when requested and only when at the very top of the page....
  const isTransparent = transparentMenu && !trigger;

  // hideElements triggers hiding of the logo, cart and menu button when searching on mobile.
  const hideElements = !isDesktop && showSearchInput;

  // Show the phat menu
  return (
    <>
      <HideOnScroll>
        <AppBar
          color="default"
          className={clxs({
            [classes.root]: true,
            [classes.transparent]: isTransparent,
          })}
          position={stickyMenu ? 'sticky' : 'fixed'}
        >
          <Toolbar
            className={clxs(classes.toolbar, {
              [classes.phatToolbar]: showPhatHeader && !trigger,
            })}
          >
            {!hideElements && <Logo />}
            <Box
              sx={{
                flexGrow: 1,
                display: 'flex',
                justifyContent: 'center',
                paddingRight: theme.spacing(1),
              }}
            >
              <AltSearchInput
                active={showSearchInput && !useDedicated}
                onIconClick={() => {}}
                onQueryChange={(ev, val) => {
                  setAnchorEl(ev.currentTarget);
                  setSearchQuery(val);
                }}
                query={searchQuery}
                onClose={() => setShowSearchInput(false)}
              />
            </Box>
            {!hideElements && (
              <>
                {!showSearchInput && (
                  <IconButton
                    sx={{
                      display: {
                        xs: 'inline-flex',
                        md: showPhatHeader ? 'none' : 'inline-flex',
                      },
                      //isTransparent ? 'none' : 'inline-flex',
                    }}
                    color="inherit"
                    aria-label="menu"
                    onClick={toggleSearch}
                    size="large"
                  >
                    <SearchIcon />
                  </IconButton>
                )}
                <IconButton color="inherit" aria-label="menu" onClick={() => history.push('/cart')} size="large">
                  <CartIcon />
                </IconButton>
                <IconButton edge="end" color="inherit" aria-label="menu" onClick={() => setShowDrawer(!showDrawer)} size="large">
                  <MenuIcon />
                </IconButton>
              </>
            )}
          </Toolbar>
        </AppBar>
      </HideOnScroll>
      <NavigationDrawer open={showDrawer} onClose={() => setShowDrawer(false)} />
      <Dialog
        open={!!anchorEl && showSearchInput}
        fullWidth
        // ={!isDesktop}
        fullScreen={!isDesktop}
        maxWidth="sm"
        disableAutoFocus
        disableEnforceFocus
        onBackdropClick={toggleSearch}
        BackdropProps={{
          sx: {
            // marginTop: '60px',
            top: 60,
          },
        }}
        PaperProps={{
          sx: {
            marginTop: {
              sm: theme.spacing(1),
            },
            // backgroundColor: 'orange',
          },
        }}
        // Container
        sx={{
          '& .MuiDialog-container': {
            alignItems: 'flex-start',
          },
          top: 60,
        }}
      >
        <>
          <QuickSearchResults query={searchQuery} onClose={toggleSearch} />
        </>
      </Dialog>
    </>
  );
}
